import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowLeftIcon,
  PencilIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  UserIcon,
  CalendarIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../../contexts/AuthContext";
import {
  getCompleteClientProfile,
  getClientStats,
  getCasesByClient,
  getClientDocuments,
} from "@/shared/db";
import { toast } from "sonner";
import { Button, CountryFlag } from "@/shared/ui";
import { getCountryName, formatDate } from "@/shared/core";
import { ClientCasesSection } from "../components/ClientCasesSection";
import { ClientDocumentsSection } from "../components/ClientDocumentsSection";
import { EditClientModal } from "../components/EditClientModal";
import { CreateCaseModal } from "../components/CreateCaseModal";
import type {
  ClientWithStats,
  ClientDocumentAssignment,
} from "../types/client.types";
import type { CaseWithDetails } from "../types/case.types";

export const ClientDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // State
  const [client, setClient] = useState<ClientWithStats | null>(null);
  const [cases, setCases] = useState<CaseWithDetails[]>([]);
  const [documents, setDocuments] = useState<ClientDocumentAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [casesLoading, setCasesLoading] = useState(true);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"cases" | "documents">("cases");

  // Stats
  const [stats, setStats] = useState({
    totalCases: 0,
    activeCases: 0,
    totalDocuments: 0,
    pendingConsultations: 0,
  });

  // Modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateCaseModalOpen, setIsCreateCaseModalOpen] = useState(false);

  useEffect(() => {
    if (clientId && user) {
      loadClientData();
    }
  }, [clientId, user]);

  const loadClientData = async () => {
    if (!clientId) return;

    try {
      setLoading(true);

      // Load client profile
      const clientData = await getCompleteClientProfile(clientId);

      if (!clientData) {
        toast.error(t("lawyer.clients.notFound", "Cliente no encontrado"));
        navigate(-1);
        return;
      }

      // Map to ClientWithStats type
      const clientWithStats: ClientWithStats = {
        id: clientData.id,
        status: clientData.status || "active",
        client_type: clientData.client_type || "individual",
        company_name: clientData.company_name || undefined,
        phone: clientData.phone || undefined,
        address: clientData.address || undefined,
        legal_help_type: clientData.legal_help_type || undefined,
        assigned_lawyer_id: clientData.assigned_lawyer_id || undefined,
        lawyer_connection_date: clientData.lawyer_connection_date || undefined,
        created_at: clientData.created_at,
        updated_at: clientData.updated_at || clientData.created_at,
        deleted_at: clientData.deleted_at || undefined,
        profiles: clientData.profiles
          ? {
              id:
                (clientData.profiles as { id?: string })?.id || clientData.id,
              full_name: clientData.profiles.full_name || "",
              email: clientData.profiles.email || "",
              country: clientData.profiles.country || "MX",
              profile_image_url:
                clientData.profiles.profile_image_url || undefined,
            }
          : null,
      };

      setClient(clientWithStats);

      // Load client stats
      const clientStats = await getClientStats(clientId);
      setStats({
        totalCases: clientStats.totalCases || 0,
        activeCases: clientStats.activeCases || 0,
        totalDocuments: clientStats.totalDocuments || 0,
        pendingConsultations: clientStats.pendingConsultations || 0,
      });

      // Load cases
      loadCases();

      // Load documents
      loadDocuments();
    } catch (error) {
      console.error("Error loading client:", error);
      toast.error(
        t(
          "lawyer.clientDetail.loadError",
          "Error al cargar los datos del cliente"
        )
      );
      navigate("/dashboard/lawyer/clients");
    } finally {
      setLoading(false);
    }
  };

  const loadCases = async () => {
    if (!clientId) return;
    setCasesLoading(true);
    try {
      const casesData = await getCasesByClient(clientId);
      // Type assertion needed due to database types being more generic
      setCases(casesData as unknown as CaseWithDetails[]);
    } catch (error) {
      console.error("Error loading cases:", error);
    } finally {
      setCasesLoading(false);
    }
  };

  const loadDocuments = async () => {
    if (!clientId) return;
    setDocumentsLoading(true);
    try {
      const docsData = await getClientDocuments(clientId);
      setDocuments(docsData as ClientDocumentAssignment[]);
    } catch (error) {
      console.error("Error loading documents:", error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    loadClientData();
    toast.success(
      t("lawyer.clients.editSuccess", "Cliente actualizado exitosamente")
    );
  };

  const handleCaseCreated = () => {
    setIsCreateCaseModalOpen(false);
    loadClientData();
    loadCases();
  };

  const handleRemoveDocument = async (
    _documentId: string,
    documentTitle: string
  ) => {
    // For now just reload - actual removal would need a dedicated function
    toast.info(
      t(
        "lawyer.clientDetail.documentRemoved",
        "Documento desvinculado: {{title}}",
        { title: documentTitle }
      )
    );
    loadDocuments();
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-tiber-200 rounded w-32" />
            <div className="bg-white rounded-2xl p-6 space-y-4">
              <div className="h-20 bg-tiber-200 rounded" />
              <div className="h-4 bg-tiber-200 rounded w-64" />
              <div className="h-4 bg-tiber-200 rounded w-48" />
            </div>
            <div className="bg-white rounded-2xl p-6 h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <UserIcon className="h-16 w-16 text-titan mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-juridico mb-2">
            {t("lawyer.clientDetail.notFound", "Cliente no encontrado")}
          </h2>
          <Button onClick={() => navigate("/dashboard/lawyer/clients")}>
            {t("lawyer.clientDetail.backToClients", "Volver a clientes")}
          </Button>
        </div>
      </div>
    );
  }

  const clientTypeBadge = {
    individual: {
      label: t("lawyer.clientTypes.individual", "Persona física"),
      color: "bg-tiber-100 text-tiber-800",
    },
    business: {
      label: t("lawyer.clientTypes.business", "Empresa"),
      color: "bg-tiber-100 text-tiber-800",
    },
    nonprofit: {
      label: t("lawyer.clientTypes.nonprofit", "Sin fines de lucro"),
      color: "bg-atlantis-100 text-atlantis-800",
    },
  };

  const statusBadge = {
    active: {
      label: t("common.active", "Activo"),
      color: "bg-atlantis-100 text-atlantis-800",
    },
    inactive: {
      label: t("common.inactive", "Inactivo"),
      color: "bg-tiber-100 text-tiber-800",
    },
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard/lawyer/clients")}
          leftIcon={<ArrowLeftIcon className="h-5 w-5" />}
          className="mb-4"
        >
          {t("lawyer.clientDetail.back")}
        </Button>

        {/* Client Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-sm border border-tiber-200 overflow-hidden mb-6"
        >
          <div className="h-40 bg-linear-to-r from-tiber-600 via-tiber-500 to-tiber-500" />
          <div className="px-8 pt-4 pb-8">
            <div className="flex flex-col md:flex-row md:items-end gap-6 -mt-12">
              <div className="shrink-0">
                <div className="h-24 w-24 rounded-2xl bg-white shadow-lg border-4 border-white flex items-center justify-center bg-linear-to-br from-tiber-500 to-tiber-500">
                  <UserIcon className="h-12 w-12 text-white" />
                </div>
              </div>

              <div className="w-full">
                <div className="w-full flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h1 className="text-2xl font-bold text-juridico">
                        {client.profiles?.full_name || t("common.unknown")}
                      </h1>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge[client.status as keyof typeof statusBadge]?.color || "bg-tiber-100 text-tiber-800"}`}
                      >
                        {statusBadge[client.status as keyof typeof statusBadge]
                          ?.label || client.status}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${clientTypeBadge[client.client_type as keyof typeof clientTypeBadge]?.color || "bg-tiber-100 text-tiber-800"}`}
                      >
                        {clientTypeBadge[
                          client.client_type as keyof typeof clientTypeBadge
                        ]?.label || client.client_type}
                      </span>
                    </div>
                    {client.company_name && (
                      <div className="flex items-center gap-2 mt-2 text-tiber-700">
                        <BuildingOfficeIcon className="h-4 w-4" />
                        <span>{client.company_name}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <Button
                      variant="ghost"
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      {t("common.edit")}
                    </Button>
                    <Button onClick={() => setIsCreateCaseModalOpen(true)}>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      {t("lawyer.clientDetail.newCase")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-tiber-200">
              <div className="flex items-center gap-3 text-tiber-700">
                <EnvelopeIcon className="h-5 w-5 text-titan" />
                <span className="truncate">
                  {client.profiles?.email || "-"}
                </span>
              </div>
              <div className="flex items-center gap-3 text-tiber-700">
                <PhoneIcon className="h-5 w-5 text-titan" />
                <span>{client.phone || "-"}</span>
              </div>
              <div className="flex items-center gap-3 text-tiber-700">
                <MapPinIcon className="h-5 w-5 text-titan" />
                <span className="flex items-center gap-2">
                  {client.profiles?.country && (
                    <CountryFlag
                      countryCode={client.profiles.country}
                      size="1em"
                    />
                  )}
                  {getCountryName(client.profiles?.country || "") || "-"}
                </span>
              </div>
              <div className="flex items-center gap-3 text-tiber-700">
                <CalendarIcon className="h-5 w-5 text-titan" />
                <span>
                  {t("lawyer.clientDetail.since", "Desde")}{" "}
                  {formatDate(client.created_at, "MMM yyyy")}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          <div className="bg-white rounded-2xl shadow-sm border border-tiber-200 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-tiber-100 rounded-xl">
                <BriefcaseIcon className="h-5 w-5 text-tiber-600" />
              </div>
              <div>
                <p className="text-xs text-titan">
                  {t("lawyer.clientDetail.stats.totalCases")}
                </p>
                <p className="text-xl font-bold text-juridico">
                  {stats.totalCases}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-tiber-200 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-atlantis-100 rounded-xl">
                <BriefcaseIcon className="h-5 w-5 text-atlantis-600" />
              </div>
              <div>
                <p className="text-xs text-titan">
                  {t("lawyer.clientDetail.stats.activeCases")}
                </p>
                <p className="text-xl font-bold text-juridico">
                  {stats.activeCases}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-tiber-200 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-tiber-100 rounded-xl">
                <DocumentTextIcon className="h-5 w-5 text-tiber-600" />
              </div>
              <div>
                <p className="text-xs text-titan">
                  {t("lawyer.clientDetail.stats.documents")}
                </p>
                <p className="text-xl font-bold text-juridico">
                  {stats.totalDocuments}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-tiber-200 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-100 rounded-xl">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-titan">
                  {t("lawyer.clientDetail.stats.consultations")}
                </p>
                <p className="text-xl font-bold text-juridico">
                  {stats.pendingConsultations}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-sm border border-tiber-200 overflow-hidden"
        >
          <div className="border-b border-tiber-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab("cases")}
                className={`flex-1 cursor-pointer px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "cases"
                    ? "border-tiber-600 text-tiber-600"
                    : "border-transparent text-titan hover:text-tiber-700 hover:border-tiber-300"
                }`}
              >
                <BriefcaseIcon className="h-5 w-5 inline mr-2" />
                {t("lawyer.clientDetail.tabs.cases")}
              </button>
              <button
                onClick={() => setActiveTab("documents")}
                className={`flex-1 cursor-pointer px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "documents"
                    ? "border-tiber-600 text-tiber-600"
                    : "border-transparent text-titan hover:text-tiber-700 hover:border-tiber-300"
                }`}
              >
                <DocumentTextIcon className="h-5 w-5 inline mr-2" />
                {t("lawyer.clientDetail.tabs.documents")}
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === "cases" && (
              <ClientCasesSection
                cases={cases}
                onCreateCase={() => setIsCreateCaseModalOpen(true)}
                loading={casesLoading}
              />
            )}
            {activeTab === "documents" && (
              <ClientDocumentsSection
                documents={documents}
                onAssignDocument={() => {}} // Could open AssignDocumentModal
                onRemoveDocument={handleRemoveDocument}
                loading={documentsLoading}
              />
            )}
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      {client && (
        <EditClientModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={handleEditSuccess}
          client={client}
        />
      )}

      <CreateCaseModal
        isOpen={isCreateCaseModalOpen}
        onClose={() => setIsCreateCaseModalOpen(false)}
        onSuccess={handleCaseCreated}
        preselectedClientId={clientId}
      />
    </div>
  );
};
